import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { viewCoupon } from "../../Redux/Actions/couponsAction";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewCoupon.css";

const ViewCoupons = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [foundCoupon, setFoundCoupon] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewCoupon(id))
      .then((res) => {
        if (res && res.statusCode === 200) {
          setFoundCoupon(res.data);
        } else {
          toast.error(res?.message || "Failed to load coupon");
        }
      })
      .catch(() => {
        toast.error("Error fetching coupon details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="vcp-loader-wrap">
          <div className="vcp-loader" />
          <p className="vcp-loader-text">Loading coupon data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!foundCoupon) {
    return (
      <MainLayout>
        <div className="vcp-empty-wrap">
          <div className="vcp-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </div>
          <p className="vcp-empty-text">No Coupon Data Found.</p>
          <Link to="/dealsAndOffers" className="vcp-back-link">← Back to Coupons</Link>
        </div>
      </MainLayout>
    );
  }

  const hasDiscounts = foundCoupon?.discounts && foundCoupon.discounts.length > 0;

  return (
    <MainLayout>
      <div className="vcp-wrapper">

        {/* ── Header ── */}
        <div className="vcp-header">
          <div className="vcp-header-left">
            <Link to="/dealsAndOffers" className="vcp-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vcp-header-divider" />
            <div>
              <div className="vcp-breadcrumb">Coupon / List / View</div>
              <h1 className="vcp-title">{foundCoupon?.code || "No Data"}</h1>
            </div>
          </div>
          <div className={`vcp-status-badge vcp-status-badge--${foundCoupon?.status?.toLowerCase()}`}>
            {ToFirstCapital(foundCoupon?.status || "")}
          </div>
        </div>

        {/* ── Hero Card ── */}
        <div className="vcp-hero-card">
          <div className="vcp-hero-code">
            <span className="vcp-hero-code-label">Coupon Code</span>
            <span className="vcp-hero-code-value">{foundCoupon?.code || "—"}</span>
            {foundCoupon?.set_primary && (
              <span className="vcp-primary-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                Primary
              </span>
            )}
          </div>
          <div className="vcp-hero-meta">
            <div className="vcp-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {DateToLocale(foundCoupon?.start_date)} — {DateToLocale(foundCoupon?.end_date)}
            </div>
            <div className="vcp-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {foundCoupon?.no_of_usage || 0} usages
            </div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="vcp-grid-layout">

          {/* Left Column */}
          <div className="vcp-col">

            {/* Project & Builder */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">01</span>
                <h2 className="vcp-card-title">Project Details</h2>
              </div>
              <div className="vcp-info-grid">
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Project Name</span>
                  <span className="vcp-info-value">{foundCoupon?.project?.project_name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Builder Name</span>
                  <span className="vcp-info-value">{foundCoupon?.builder?.brand_name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Catalogue</span>
                  <span className="vcp-info-value">{foundCoupon?.catalogue?.tower_name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Project Category</span>
                  <span className="vcp-info-value">{foundCoupon?.project_categories?.[0]?.name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Use For</span>
                  <span className="vcp-info-value">{ToFirstCapital(foundCoupon?.use_for) || "No Data"}</span>
                </div>
                {foundCoupon?.use_for === "agent" && (
                  <div className="vcp-info-item">
                    <span className="vcp-info-label">Agent</span>
                    <span className="vcp-info-value">
                      {foundCoupon?.agent?.name || foundCoupon?.agent?.full_name || foundCoupon?.agent?.email || "No Data"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Validity & Usage */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">02</span>
                <h2 className="vcp-card-title">Validity & Usage</h2>
              </div>
              <div className="vcp-info-grid">
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Valid From</span>
                  <span className="vcp-info-value">{DateToLocale(foundCoupon?.start_date)}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Valid Till</span>
                  <span className="vcp-info-value">{DateToLocale(foundCoupon?.end_date)}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">No. of Usages</span>
                  <span className="vcp-info-value">{foundCoupon?.no_of_usage || 0}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Status</span>
                  <span className={`vcp-inline-status vcp-inline-status--${foundCoupon?.status?.toLowerCase()}`}>
                    {ToFirstCapital(foundCoupon?.status)}
                  </span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Primary Status</span>
                  <span className={`vcp-inline-status ${foundCoupon?.set_primary ? "vcp-inline-status--primary" : "vcp-inline-status--inactive"}`}>
                    {foundCoupon?.set_primary === true ? "Primary" : "Not Primary"}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">03</span>
                <h2 className="vcp-card-title">Description</h2>
              </div>
              <p className="vcp-description">
                {foundCoupon?.description || "No Description Available"}
              </p>
            </div>

          </div>

          {/* Right Column */}
          <div className="vcp-col">

            {/* Discount — single (no discounts array) */}
            {!hasDiscounts && (
              <div className="vcp-card">
                <div className="vcp-card-header">
                  <span className="vcp-card-badge">04</span>
                  <h2 className="vcp-card-title">Discount Configuration</h2>
                </div>
                <div className="vcp-discount-single">
                  <div className="vcp-discount-pill">
                    <span className="vcp-discount-type">
                      {foundCoupon?.discount_type ? ToFirstCapital(foundCoupon.discount_type) : "—"}
                    </span>
                    <span className="vcp-discount-val">
                      {foundCoupon?.discount_in === "percent"
                        ? `${foundCoupon.value}%`
                        : foundCoupon?.value
                          ? `₹${foundCoupon.value}`
                          : "No Data"}
                    </span>
                  </div>
                  {foundCoupon?.value_upto_show > 0 && (
                    <div className="vcp-info-item" style={{ marginTop: "12px" }}>
                      <span className="vcp-info-label">Show Upto</span>
                      <span className="vcp-info-value">₹{foundCoupon.value_upto_show}</span>
                    </div>
                  )}
                  {foundCoupon?.max_value > 0 && (
                    <div className="vcp-info-item">
                      <span className="vcp-info-label">Max Value</span>
                      <span className="vcp-info-value">₹{foundCoupon.max_value}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Discount — per unit */}
            {hasDiscounts && (
              <div className="vcp-card">
                <div className="vcp-card-header">
                  <span className="vcp-card-badge">04</span>
                  <h2 className="vcp-card-title">Discounts Per Unit</h2>
                  <span className="vcp-count-chip">{foundCoupon.discounts.length}</span>
                </div>
                <div className="vcp-discount-list">
                  {foundCoupon.discounts.map((disc) => (
                    <div key={disc._id} className="vcp-discount-row">
                      <div className="vcp-discount-row-unit">{disc.unit}</div>
                      <div className="vcp-discount-row-detail">
                        <span className="vcp-discount-type">{ToFirstCapital(disc.discount_type)}</span>
                        <span className="vcp-discount-val">
                          {disc.discount_in === "percent" ? `${disc.value}%` : `₹${disc.value}`}
                        </span>
                        {disc.value_upto_show > 0 && (
                          <span className="vcp-discount-upto">upto ₹{disc.value_upto_show}</span>
                        )}
                        {disc.max_value > 0 && (
                          <span className="vcp-discount-max">max ₹{disc.max_value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Units Info */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">05</span>
                <h2 className="vcp-card-title">Units Information</h2>
                {foundCoupon?.unitno?.length > 0 && (
                  <span className="vcp-count-chip">{foundCoupon.unitno.length} units</span>
                )}
              </div>
              {foundCoupon?.unitno && foundCoupon.unitno.length > 0 ? (
                <div className="vcp-units-grid">
                  {foundCoupon.unitno.map((unit) => (
                    <div key={unit.catalogue_unit_id} className="vcp-unit-card">
                      <div className="vcp-unit-card-header">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M9 9h6M9 12h6M9 15h4" />
                        </svg>
                        Unit {unit.unitno}
                      </div>
                      <div className="vcp-unit-detail">
                        <span className="vcp-unit-label">Floor</span>
                        <span className="vcp-unit-val">{unit.floor}</span>
                      </div>
                      <div className="vcp-unit-detail">
                        <span className="vcp-unit-label">Furnishing</span>
                        <span className="vcp-unit-val">{unit.furnishingstatus || "N/A"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="vcp-no-data">No Unit Information Available</p>
              )}
            </div>

          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ViewCoupons;